import React, { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Plane, Globe, Check, ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { CHAPTERS } from "../../data/content";
import api from "../../lib/api";
import { toast } from "sonner";

const Stamp = ({ place, year }) => (
  <div className="relative w-24 h-24 shrink-0">
    <motion.div
      initial={{ opacity: 0, scale: 2, rotate: -28 }}
      whileInView={{ opacity: 0.9, scale: 1, rotate: -12 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, ease: "backOut" }}
      className="absolute inset-0 rounded-full border-[3px] border-stone/70 flex flex-col items-center justify-center text-stone"
      style={{ boxShadow: "inset 0 0 0 2px rgba(184,177,168,0.25)" }}
    >
      <span className="heading text-lg leading-none">{(place.includes(",") ? place.split(",").pop().trim() : place).slice(0, 3).toUpperCase()}</span>
      <span className="text-[0.55rem] tracking-[0.2em] mt-1">{year}</span>
      <span className="text-[0.5rem] tracking-[0.3em] mt-0.5 opacity-70">ENTRY</span>
    </motion.div>
  </div>
);

export const Journey = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start center", "end end"],
  });
  const planeX = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  const lineScale = useTransform(scrollYProgress, [0, 1], [0, 1]);

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/subscribe", { email });
      toast.success(data.message || "You're on the list.");
      setEmail("");
    } catch (err) {
      toast.error("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="journey"
      data-testid="journey-section"
      ref={sectionRef}
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-16 overflow-hidden bg-matte"
    >
      {/* cartographic background */}
      <div
        className="absolute inset-0 opacity-[0.12] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle, rgba(184,177,168,0.6) 1px, transparent 1px)",
          backgroundSize: "26px 26px",
          maskImage: "radial-gradient(ellipse at center, black 40%, transparent 85%)",
        }}
      />

      <div className="relative">
        <Reveal>
          <div className="flex items-center gap-4 mb-4">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, duration: 18, ease: "linear" }}
            >
              <Globe className="text-stone" size={22} strokeWidth={1.5} />
            </motion.div>
            <p className="eyebrow !mb-0">The Journey</p>
          </div>
          <h2 className="heading text-5xl sm:text-6xl lg:text-8xl text-cream mb-4">
            Stamped By Every City.
          </h2>
          <p className="text-cream/60 max-w-xl">
            Thin lines on a map. New flags. New chapters. Follow the route from the first leap
            to wherever it's going next.
          </p>
        </Reveal>

        {/* Plane progress line */}
        <div className="relative mt-16 mb-12 hidden md:block">
          <div className="h-px w-full bg-silver/15" />
          <motion.div
            style={{ scaleX: lineScale, transformOrigin: "left" }}
            className="absolute top-0 left-0 h-px w-full bg-stone"
          />
          <motion.div
            style={{ left: planeX }}
            className="absolute -top-3 -ml-3"
          >
            <Plane className="text-stone rotate-45" size={22} />
          </motion.div>
        </div>

        {/* Chapters */}
        <div className="grid md:grid-cols-3 gap-10 lg:gap-14 mt-10">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.place} delay={i * 0.12}>
              <article
                data-testid={`chapter-${i}`}
                className="group flex flex-col h-full border-t border-silver/15 pt-8"
              >
                <div className="flex items-start justify-between gap-3 mb-6">
                  <div>
                    <div className="text-3xl mb-2">{c.flag}</div>
                    <h3 className="heading text-3xl text-cream">{c.place}</h3>
                    <p className="text-silver/60 text-sm tracking-[0.2em]">{c.year}</p>
                  </div>
                  <Stamp place={c.place} year={c.year} />
                </div>

                <div className="relative overflow-hidden mb-6 aspect-[4/3]">
                  <img
                    src={c.image}
                    alt={c.place}
                    className="w-full h-full object-cover object-top film-warm group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-matte/30" />
                </div>

                <p className="heading text-3xl text-stone mb-4">{c.headline}</p>
                <div className="space-y-3 text-cream/70 text-sm leading-relaxed mb-6">
                  {c.copy.map((p, j) => (
                    <p key={j}>{p}</p>
                  ))}
                </div>

                {c.achievements.length > 0 && (
                  <ul className="mt-auto space-y-2">
                    {c.achievements.map((a) => (
                      <li key={a} className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-cream/80">
                        <Check size={14} className="text-stone shrink-0" /> {a}
                      </li>
                    ))}
                  </ul>
                )}
              </article>
            </Reveal>
          ))}
        </div>

        {/* Email capture */}
        <Reveal>
          <div className="mt-24 border border-silver/15 bg-charcoal/30 p-8 md:p-14 text-center max-w-3xl mx-auto">
            <p className="eyebrow mb-4">Stay Connected</p>
            <h3 className="heading text-4xl md:text-5xl text-cream mb-3">
              Follow Jaron's Journey.
            </h3>
            <p className="text-cream/60 mb-8">Schedule updates, new highlights, and behind-the-scenes from every city. Straight to your inbox.</p>
            <form onSubmit={submit} className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
              <input
                data-testid="subscribe-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="field text-center sm:text-left"
              />
              <button data-testid="subscribe-btn" disabled={loading} className="btn-primary whitespace-nowrap">
                {loading ? "…" : <>Keep Me Updated <ArrowRight size={16} /></>}
              </button>
            </form>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
